import { Order } from '@customTypes/order';

import { PRODUCT_ID_SECONDARY_FINDINGS, PRODUCT_ID_SECONDARY_FINDINGS_NATERA } from '@constants/constants';

const productNamingText = (order: Order): string =>
  [
    order.product_name,
    order.product_family_name,
    order.product_test_name,
    order.product_test_type,
    order.product_test_subclass,
  ]
    .filter(Boolean)
    .join(' ');

/**
 * Checks if an order is a Secondary Findings product
 * @param order - The order to check
 * @returns true for known Secondary Findings IDs or naming fields containing
 * "secondary findings"
 */
export function isSecondaryFindings(order: Order): boolean {
  return (
    order.product_id === PRODUCT_ID_SECONDARY_FINDINGS ||
    order.product_id === PRODUCT_ID_SECONDARY_FINDINGS_NATERA ||
    /secondary findings/i.test(productNamingText(order))
  );
}

/**
 * Checks if an order is a Rare Disease product
 * @param order - The order to check
 * @returns true for rare-disease report types, a PR51 product id, or a naming field containing "rare disease"
 */
export function isRareDiseaseProduct(order: Order): boolean {
  return (
    order.report_type === 'RARE_DISEASE' ||
    order.report_type === 'NDD_CNA' ||
    /^PR51/.test(order.product_id) ||
    /rare disease/i.test(productNamingText(order))
  );
}

export function isMedicationResponseOrder(order: Order): boolean {
  return /medication response|pharmacogen/i.test(productNamingText(order));
}

/**
 * Checks if an order is a Proactive Health / Atlas product
 * @param order - The order to check
 * @returns true if product is proactive (excludes Secondary Findings and Rare Disease)
 */
export function isProactiveProduct(order: Order): boolean {
  if (isSecondaryFindings(order) || isRareDiseaseProduct(order)) return false;

  // Structured Atlas payloads identify the proactive family directly. The
  // canonical name checks retain support for name-only Atlas and legacy MyOme payloads.
  if (/\batlas\b/i.test(order.product_family_name ?? '') || /proactive health|\batlas\b/i.test(order.product_name)) {
    return true;
  }

  // ID-based proactive (PR2/PR3/PR4/PR6), excluding Rare (PR51)
  return /^PR[2346]/.test(order.product_id) && !/^PR51/.test(order.product_id);
}
